// ./src/components/features/DashboardCards/DashboardCardsDrawer.tsx
//
// ROADMAP #190 — choose, hide and order the event dashboard's cards (owner, 2026-10-20: "user should be able to
// enable/disable/reorder widgets on the dashboard on event level and also account level"). ONE drawer for both levels:
//   · an EVENT's (`eventId`) — opened from the dashboard's ⋯ menu, a URL stage of that page (`/events/:id/customize`).
//     Its list is the event's own, else the account's default; "Use the account's cards" drops the event's own list, so a
//     later change to the default reaches it again.
//   · the ACCOUNT's default (no `eventId`) — from Event defaults (`/settings/preferences/dashboard`), for every event
//     without a list of its own; "Use the standard cards" clears it.
//
// ⚠ READ FRESH ON EVERY OPEN and SAVED BY ITS OWN MUTATION (never through `settings`: the server keeps the event's list
//   out of the ordinary event update — the app sends `settings` whole, from older copies).
// ⚠ A failed read is an error, never an empty list — saving over it would replace a list the host could not see.
// ⓘ An event's drawer lists only the cards that exist on it (Add-ons with tickets only); the others keep their places.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Group, Loader, Stack, Text } from "@mantine/core";
import type { OpResult } from "@sdflc/api-helpers";
import { useTranslation } from "react-i18next";

import { AppDrawer } from "@/components/generic/AppDrawer";
import { AppIcon } from "@/components/generic/AppIcon";
import { ErrorAlert } from "@/components/generic/ErrorAlert";
import { useFormSnapshot, useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";
import { AccountPreferenceService, EventService } from "@/utils/api";
import type { AccountPreference, EventDashboardCards } from "@/utils/api";
import { getFormErrors } from "@/utils";
import {
  DASHBOARD_CARDS_SOURCE,
  completeDashboardCards,
  dashboardCardApplies,
  defaultDashboardCards,
  mergeShownCards,
  normaliseDashboardCards,
  sameDashboardCards,
  type DashboardCardEventFacts,
  type DashboardCardId,
  type DashboardCardSetting,
} from "@/utils/dashboardCards";

import { DashboardCardsEditor } from "./DashboardCardsEditor";

/** The list itself is the only field: its refusal (`cards` / `dashboardCards`) is said above the list. */
const KNOWN_FORM_FIELDS = ["cards", "dashboardCards"] as const;

export interface DashboardCardsDrawerProps {
  opened: boolean;
  onClose: () => void;
  /** The event's dashboard; absent for the account's default. */
  eventId?: string;
  /** The event's kind (which cards exist on it). Required with `eventId`. */
  ticketed?: boolean;
  /** After a save: the list the dashboard now draws. */
  onSaved?: (cards: DashboardCardSetting[]) => void;
}

interface Loaded {
  /** The whole list in force. */
  cards: DashboardCardSetting[];
  /** The event's own list is what is in force (an event's drawer only). */
  own: boolean;
  /** What "use the …" comes back to. */
  fallback: DashboardCardSetting[];
  /** The fallback is the account's (else the built-in). */
  fallbackIsAccount: boolean;
}

const DashboardCardsDrawerComponent: React.FC<DashboardCardsDrawerProps> = ({ opened, onClose, eventId, ticketed = false, onSaved }) => {
  const { t } = useTranslation(["events", "common"]);
  const forEvent = !!eventId;
  const facts = useMemo<DashboardCardEventFacts | null>(() => (forEvent ? { ticketed } : null), [forEvent, ticketed]);
  const applies = useCallback((id: DashboardCardId) => dashboardCardApplies(id, facts), [facts]);

  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<OpResult | null>(null);
  // The cards the drawer shows, in order (the ones that apply here).
  const [draft, setDraft] = useState<DashboardCardSetting[]>([]);
  // "Use the account's / standard cards" was pressed (and nothing moved since): the save drops the list.
  const [useFallback, setUseFallback] = useState(false);

  const { isDirty, seedFrom, clear } = useFormSnapshot({ draft, useFallback });

  // Only the latest read may land: one still in flight from an earlier open is dropped.
  const request = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    const mine = ++request.current;

    setLoading(true);
    setLoadError(null);
    setLoaded(null);

    const result = forEvent ? await EventService.dashboardCards(eventId as string) : await AccountPreferenceService.getDashboardCards();

    if (mine !== request.current) {
      return;
    }

    setLoading(false);

    if (!result.didSucceed()) {
      setLoadError(result);

      return;
    }

    let next: Loaded;

    if (forEvent) {
      const row = result.getDataFirst() as EventDashboardCards | undefined;

      next = {
        cards: completeDashboardCards(normaliseDashboardCards(row?.cards)),
        own: row?.source === DASHBOARD_CARDS_SOURCE.EVENT,
        fallback: completeDashboardCards(normaliseDashboardCards(row?.accountCards)),
        fallbackIsAccount: row?.accountSource === DASHBOARD_CARDS_SOURCE.ACCOUNT,
      };
    } else {
      const row = result.getDataFirst() as AccountPreference | undefined;
      const stored = normaliseDashboardCards(row?.dashboardCards);

      next = { cards: completeDashboardCards(stored), own: !!stored, fallback: defaultDashboardCards(), fallbackIsAccount: false };
    }

    const shown = next.cards.filter((card) => applies(card.id));

    setLoaded(next);
    setDraft(shown);
    setUseFallback(false);
    seedFrom({ draft: shown, useFallback: false });
  }, [forEvent, eventId, applies, seedFrom]);

  useEffect(() => {
    if (!opened) {
      request.current += 1;
      setLoading(false);
      clear();

      return;
    }

    setSaveError(null);
    void load();
  }, [opened, load, clear]);

  const change = useCallback((cards: DashboardCardSetting[]) => {
    setDraft(cards);
    setUseFallback(false);
  }, []);

  const backToFallback = useCallback(() => {
    if (!loaded) {
      return;
    }

    setDraft(loaded.fallback.filter((card) => applies(card.id)));
    setUseFallback(true);
  }, [loaded, applies]);

  const handleSave = useCallback(async () => {
    if (!loaded || saving || loading) {
      return;
    }

    setSaving(true);
    setSaveError(null);

    // The whole list: the arranged cards in the slots of the ones shown, the others where they were.
    const whole = useFallback ? null : mergeShownCards(loaded.cards, draft, applies);
    const result = forEvent
      ? await EventService.saveDashboardCards(eventId as string, whole)
      : await AccountPreferenceService.saveDashboardCards(whole);

    setSaving(false);

    if (!result.didSucceed()) {
      setSaveError(result);

      return;
    }

    // ⓘ The dashboard draws what the server now resolves to (the account's list, after "use the account's").
    const saved = forEvent
      ? normaliseDashboardCards((result.getDataFirst() as EventDashboardCards | undefined)?.cards)
      : normaliseDashboardCards((result.getDataFirst() as AccountPreference | undefined)?.dashboardCards);

    clear();
    onSaved?.(completeDashboardCards(saved ?? (useFallback ? loaded.fallback : whole)));
    onClose();
  }, [loaded, saving, loading, useFallback, draft, applies, forEvent, eventId, clear, onSaved, onClose]);

  // ⌘S / ⌘Enter. Esc is AppDrawer's own (it asks before discarding a changed list).
  useModalShortcuts({ onConfirm: handleSave, canConfirm: !!loaded && !loading && !saving && isDirty, enabled: opened });

  const { fields, genericErrors } = getFormErrors(saveError, KNOWN_FORM_FIELDS);
  const listError = fields.cards ?? fields.dashboardCards ?? null;
  const fallbackSame = !!loaded && sameDashboardCards(draft, loaded.fallback.filter((card) => applies(card.id)));
  // "Use the …" is offered while the drawer is not already there.
  const offerFallback = !!loaded && !useFallback && (loaded.own || !fallbackSame);

  const fallbackLabel = forEvent
    ? loaded?.fallbackIsAccount
      ? t("events:dashCards.useAccount", { defaultValue: "Use the account's default" })
      : t("events:dashCards.useStandard", { defaultValue: "Use the standard cards" })
    : t("events:dashCards.useStandard", { defaultValue: "Use the standard cards" });

  const sourceLine = (() => {
    if (!loaded) {
      return null;
    }

    if (useFallback) {
      return forEvent && loaded.fallbackIsAccount
        ? t("events:dashCards.willFollowAccount", { defaultValue: "Saved, this event follows the account's default cards again — and any later change to them." })
        : t("events:dashCards.willBeStandard", { defaultValue: "Saved, the standard cards are used." });
    }

    if (!forEvent) {
      return t("events:dashCards.accountIntro", {
        defaultValue: "The cards every event's dashboard starts with. An event you arrange on its own dashboard keeps its own.",
      });
    }

    if (loaded.own) {
      return t("events:dashCards.eventOwn", { defaultValue: "This event has its own arrangement." });
    }

    return loaded.fallbackIsAccount
      ? t("events:dashCards.eventFollowsAccount", { defaultValue: "This event uses the account's default cards. Saving here gives it its own." })
      : t("events:dashCards.eventStandard", { defaultValue: "This event uses the standard cards. Saving here gives it its own." });
  })();

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={
        forEvent
          ? t("events:dashCards.titleEvent", { defaultValue: "Customize the dashboard" })
          : t("events:dashCards.titleAccount", { defaultValue: "Dashboard cards for every event" })
      }
      size="md"
      closable={!saving}
      loading={loading || saving}
      actionType="save-cancel"
      onConfirm={handleSave}
      confirmLabel={t("common:buttons.save", { defaultValue: "Save" })}
      confirmShortcut={SHORTCUT_HINTS.save}
      // ⚠ Nothing changed, nothing to save: a Save here would quietly freeze a copy of the account's cards as the event's
      // own, and later changes to the default would stop reaching it (#190 review).
      confirmDisabled={!loaded || loading || !isDirty}
      isDirty={isDirty}
      errors={genericErrors.length > 0 ? genericErrors : null}
      // The list's own refusal is drawn above the list (which may be scrolled away): the footer points to it.
      hasFieldErrors={!!listError}
    >
      <Stack gap="md">
        {loading && (
          <Group gap="xs">
            <Loader size="sm" />
            <Text size="md" c="dimmed">
              {t("events:dashCards.loading", { defaultValue: "Reading the cards…" })}
            </Text>
          </Group>
        )}

        {loadError && (
          <ErrorAlert
            error={loadError}
            title={t("events:dashCards.loadFailed", { defaultValue: "We could not read the dashboard cards" })}
            page="Dashboard cards"
            operation={forEvent ? "eventDashboardCards" : "accountPreferenceGet"}
          />
        )}

        {loaded && !loading && (
          <>
            {/* ⓘ "Use the …" sits with the line that says where the cards come from — not in the footer, where a third
                button pushed Save off a phone's screen. */}
            {(sourceLine || offerFallback) && (
              <Stack gap="xs" align="flex-start">
                {sourceLine && (
                  <Text size="md" c="dimmed">
                    {sourceLine}
                  </Text>
                )}
                {offerFallback && (
                  <Button variant="light" size="md" leftSection={<AppIcon iconName="ArrowBackUp" size={16} />} onClick={backToFallback} disabled={saving}>
                    {fallbackLabel}
                  </Button>
                )}
              </Stack>
            )}
            <Text size="md" c="dimmed">
              {t("events:dashCards.howTo", {
                defaultValue: "Drag a card to move it; switch it off to hide it. A card that is on still stays out of the way until it has something to show.",
              })}
            </Text>

            {listError && (
              <Alert color="red" variant="light">
                <Text size="md">{listError}</Text>
              </Alert>
            )}

            <DashboardCardsEditor value={draft} onChange={change} facts={facts} disabled={saving} />
          </>
        )}
      </Stack>
    </AppDrawer>
  );
};

export const DashboardCardsDrawer = React.memo(DashboardCardsDrawerComponent);
